import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import styles from './styles.module.scss';
import Loader from '../../common/Loader';
import { parseDate } from '../../../helpers/date';
import { Helmet } from 'react-helmet';
import InsertDriveFileRoundedIcon from '@material-ui/icons/InsertDriveFileRounded';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import { toggleBackButtonAction } from '../../../reactRedux/Actions/sideBarAction';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  announcementRequest,
  sendAnnouncementReactionRequest,
  deleteAnnouncementReactionRequest,
  deleteAnnouncementCommentRequest,
  editAnnouncementCommentRequest,
  getAnnouncementCommentsRequest,
  sendAnnouncementCommentRequest
} from './api';
import './textFormatStyle.css';
import { IconButton } from '@material-ui/core';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import IosShareIcon from '@mui/icons-material/IosShare';
import classNames from 'classnames';
import Comment from '../../common/comments';
import { get_user_info } from '../../../helpers/api';
import { useHistory, generatePath } from 'react-router-dom';
import { routes } from '../../../assets/routes';
import { TextField } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useTranslation } from '../../../i18n/LanguageProvider';

const safeJson = (json) => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return json;
  }
};

const Announcement = (props) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const dispatch = useDispatch();
  const [id] = useState(props.location.pathname.slice(1).split('/')[1]);
  const history = useHistory();

  const myId = get_user_info()?.id;

  const { isLoading, data, refetch } = useQuery(
    ['announcement', id],
    () => announcementRequest(id),
    {
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const {
    isLoading: commentsIsLoading,
    data: comments,
    refetch: refetchComments
  } = useQuery(
    ['announcement', 'comments', id],
    () => getAnnouncementCommentsRequest(id),
    {
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const sendAnnouncementReactionMutation = useMutation(
    sendAnnouncementReactionRequest,
    {
      onSuccess() {
        refetch();
      },
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const deleteAnnouncementReactionMutation = useMutation(
    deleteAnnouncementReactionRequest,
    {
      onSuccess() {
        refetch();
      }
    }
  );

  const sendCommentMutation = useMutation(sendAnnouncementCommentRequest, {
    onSuccess() {
      refetchComments();
      setNewComment('');
      toast.success(t('comments.sent'));
    },
    onError(error) {
      toast.error(error.response?.data?.detail);
    }
  });

  const deleteCommentRequestMutation = useMutation(
    deleteAnnouncementCommentRequest,
    {
      onSuccess() {
        refetchComments();
        toast.success(t('comments.deleted'));
      },
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const editCommentRequestMutation = useMutation(
    editAnnouncementCommentRequest,
    {
      onSuccess() {
        refetchComments();
        toast.success(t('comments.edited'));
      },
      onError(error) {
        toast.error(error.response?.data?.detail);
      }
    }
  );

  const setSideBarBackButton = (show) => dispatch(toggleBackButtonAction(show));
  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));

  useEffect(() => {
    setSideBarBackButton(true);
    setHeader('');
    setHeaderConfigs({ credits: false, exit: false, back: true, logo: true });
    return () => setSideBarBackButton(false);
  }, []);

  return (
    <div className={styles.page}>
      {isLoading ? (
        <Loader size={40} />
      ) : (
        <>
          <Helmet>
            <title>{data.title}</title>
          </Helmet>
          <div className={styles.container}>
            <h2 dir="auto">{data.title}</h2>

            <div className={styles.description}>
              <span className={styles.time}>{parseDate(data.created_at)}</span>
              <span>|</span>
              <span className={styles.view}>
                <RemoveRedEyeIcon sx={{ fontSize: 12 }} />
                {data.announcement_view_count}
              </span>
            </div>
            <div>
              {data?.cover?.absolute_url && (
                <img
                  className={styles.cover}
                  src={data?.cover?.absolute_url}
                  alt={`${data?.title} Announcement Cover`}
                />
              )}
            </div>
            <div
              className={styles.body}
              dir="auto"
              dangerouslySetInnerHTML={{
                __html: safeJson(data.body)?.html || data.body
              }}
            ></div>
            <div className={styles.attachments}>
              {data.attachments.map((a, index) => (
                <a
                  key={index}
                  href={a.absolute_url}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.attachWrapper}
                >
                  <div className={styles.icon}>
                    {!!a?.thumbnail && (
                      <img
                        className={styles.thumb}
                        src={a.thumbnail}
                        alt="thumbnail"
                      />
                    )}
                    <InsertDriveFileRoundedIcon />
                  </div>
                  <span className={styles.filename}>
                    {a?.filename || t('announcements.attachment')}
                  </span>
                  <GetAppRoundedIcon className={styles.download} />
                </a>
              ))}
            </div>
            <div className={styles.actions}>
              <div className={styles.number}>
                <IconButton
                  disabled={
                    sendAnnouncementReactionMutation.isLoading &&
                    deleteAnnouncementReactionMutation.isLoading
                  }
                  onClick={() => {
                    if (data.my_reaction !== 'like') {
                      sendAnnouncementReactionMutation.mutate({
                        id: data.id,
                        reaction_type: 'like'
                      });
                    } else {
                      deleteAnnouncementReactionMutation.mutate({
                        id: data.id
                      });
                    }
                  }}
                  style={{
                    padding: '6px'
                  }}
                >
                  {data.my_reaction === 'like' ? (
                    <ThumbUpIcon />
                  ) : (
                    <ThumbUpOffAltIcon />
                  )}
                </IconButton>
                {data.reaction_response.reactions_count.like > 0 &&
                  data.reaction_response.reactions_count.like}
              </div>
              <div className={styles.number}>
                <IconButton
                  disabled={
                    sendAnnouncementReactionMutation.isLoading &&
                    deleteAnnouncementReactionMutation.isLoading
                  }
                  onClick={() => {
                    if (data.my_reaction !== 'dislike') {
                      sendAnnouncementReactionMutation.mutate({
                        id: data.id,
                        reaction_type: 'dislike'
                      });
                    } else {
                      deleteAnnouncementReactionMutation.mutate({
                        id: data.id
                      });
                    }
                  }}
                  style={{
                    padding: '6px'
                  }}
                >
                  {data.my_reaction === 'dislike' ? (
                    <ThumbDownIcon />
                  ) : (
                    <ThumbDownOffAltIcon />
                  )}
                </IconButton>
                {data.reaction_response.reactions_count.dislike > 0 &&
                  data.reaction_response.reactions_count.dislike}
              </div>
            </div>

            <div className={classNames(styles.noMargin, styles.sectionBody)}>
              {commentsIsLoading ? (
                <Loader />
              ) : (
                comments?.map((comment) => (
                  <Comment
                    key={comment.id}
                    comment={comment.comment}
                    createdAt={comment.created_at}
                    editable={myId === comment.sender.id}
                    loading={
                      editCommentRequestMutation.isLoading ||
                      deleteCommentRequestMutation.isLoading
                    }
                    senderUser={{
                      first_name: comment.sender.first_name,
                      last_name: comment.sender.last_name,
                      avatar: comment.sender.avatar?.thumbnail
                    }}
                    onDeleteComment={() => {
                      deleteCommentRequestMutation.mutate({
                        id: comment.id
                      });
                    }}
                    onEditComment={(newComment) =>
                      editCommentRequestMutation.mutate({
                        comment: newComment,
                        id: comment.id
                      })
                    }
                    onProfileClick={() => {
                      history.push(
                        generatePath(routes.PROFILE, {
                          id: comment.sender.id
                        })
                      );
                    }}
                  />
                ))
              )}
            </div>

            <form
              className={styles.comment}
              onSubmit={(e) => {
                e.preventDefault();
                sendCommentMutation.mutate({
                  id,
                  comment: newComment
                });
              }}
            >
              <TextField
                onChange={(e) => setNewComment(e.target.value)}
                name="comment"
                value={newComment}
                fullWidth
                label={t('comments.placeholder')}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      disabled={sendCommentMutation.isLoading}
                      type="submit"
                    >
                      {!!newComment && <IosShareIcon color="primary" />}
                    </IconButton>
                  )
                }}
              />
            </form>
          </div>
        </>
      )}
    </div>
  );
};

Announcement.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  })
};

export default Announcement;
